import { useEffect, useState } from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import { mfaSetupRequest, mfaSetupValidateRequest } from "../../utils/APICalls";
import ErrorNotification from "../../components/error-notification/error-notification";
import Loading from "../../components/loading/loading";
import OtpInput from "react-otp-input";
import QRCode from "react-qr-code";

export default function MFASetUp() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);
  const [mfaResponse, setMfaResponse] = useState<any>();
  const [otp, setOtp] = useState("");
  const [showSecretCode, setShowSecretCode] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (state) {
      const payload = {
        session: state?.session,
      };
      mfaSetup(payload);
    }
  }, [state]);

  const mfaSetup = async (payload: any) => {
    setShowLoading(true);
    const response = await mfaSetupRequest(payload);
    setShowLoading(false);
    if (response?.status === 200) {
      setMfaResponse(response?.data);
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const mfaValidate = async () => {
    const payload = {
      code: otp,
      session: mfaResponse?.session,
    };
    setShowLoading(true);
    const response = await mfaSetupValidateRequest(payload);
    setShowLoading(false);
    if (response?.status === 200) {
      sessionStorage.setItem("accessToken", response?.data?.accessToken);
      sessionStorage.setItem("refreshToken", response?.data?.refreshToken);
      navigate("/");
    } else if (response?.status === 400 || response?.status === 404) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <Box className="backgroud-layout">
      <Box className="signin-layout">
        <Card className="signin-card">
          <img
            className="signin-logo"
            src={require("../../assets/Vision_Logo.svg").default}
            alt="logo"
          />
          <Typography className="signin-text" variant="h5">
            Set Up Authenticator App MFA
          </Typography>
          <Typography sx={{ mx: 3, mt: 3, textAlign: "left" }}>
            Complete your registration by entering the code from your
            authenticator app to set up MFA. This will be used when signing in
            to verify your identity.
          </Typography>
          <Box sx={{ display: "flex", mt: 3, mx: 3 }}>
            <Typography>1.</Typography>
            <Typography sx={{ ml: 0.5, mr: 2, textAlign: "left" }}>
              Install an authenticator app on your mobile device such as Google
              Authenticator or Microsoft Authenticator.
            </Typography>
            <PhoneAndroidOutlinedIcon
              sx={{ width: 80, height: 80, color: "rgba(0, 0, 0, 0.6)" }}
            />
          </Box>
          <Box sx={{ display: "flex", mt: 3, mx: 3 }}>
            <Typography>2.</Typography>
            <Box sx={{ mr: 2, textAlign: "left", width: "calc(100% - 112px)" }}>
              <Typography sx={{ ml: 0.5, textAlign: "left" }}>
                Scan this QR code with your authenticator app, or manually enter
                a secret key.
              </Typography>
              {showSecretCode && (
                <Typography sx={{ mt: 0.5, wordBreak: "break-all" }}>
                  Secret Key: {mfaResponse?.secretCode}
                </Typography>
              )}
              <Button
                sx={{ fontWeight: "510", mt: 1 }}
                onClick={() => setShowSecretCode((prev) => !prev)}
              >
                {showSecretCode ? "Hide Secret Key" : "Show Secret Key"}
              </Button>
            </Box>
            <Box sx={{ width: 80, height: 80 }}>
              {mfaResponse?.qrCodeUri && (
                <QRCode
                  size={256}
                  style={{ width: 80, height: 80 }}
                  value={mfaResponse?.qrCodeUri}
                  viewBox={`0 0 256 256`}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", mt: 3, mx: 3 }}>
            <Typography>3.</Typography>
            <Typography sx={{ ml: 0.5, mr: 2, textAlign: "left" }}>
              Enter the 6 digit code from the authenticator app
            </Typography>
          </Box>
          <OtpInput
            containerStyle={{ margin: "8px 24px 0px 28px" }}
            inputStyle={{
              height: 56,
              width: 56,
              marginLeft: 14,
              borderColor: "rgba(0, 0, 0, 0.23)",
              borderWidth: 1,
              borderRadius: 4,
              fontSize: 34,
            }}
            value={otp}
            onChange={setOtp}
            numInputs={6}
            inputType="tel"
            renderInput={(props) => <input {...props} />}
          />
          <Box sx={{ display: "flex", mt: 3, mx: 3 }}>
            <Button
              sx={{ flex: 1, mr: 2, height: 42 }}
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button
              sx={{ flex: 1, ml: 2, height: 42 }}
              disabled={otp.length !== 6}
              variant="contained"
              onClick={mfaValidate}
            >
              Sign In
            </Button>
          </Box>
        </Card>
      </Box>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoading && <Loading />}
    </Box>
  );
}
